import { TableOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import { Routes } from 'config/routes';
import { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import DonorDetailsHeader from './components/DonorDetailsHeader';
import DonorTableView from './components/DonorTableView';
import RecruiterDonorsView from './components/RecruiterDonorsView';
import RecruiterLocationsView from './components/RecruiterLocationsView';
import UnitTableView from './components/UnitTableView';
import { useUrlParams } from './hooks/useUrlParams';
import { buildTableMeta } from './utils/buildTableMeta';

import { useRecruiterDashboard } from '../../contexts/RecruiterDashboardContext';

export const DonorDetailsStubContent: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    table,
    filters,
    recruiter: { recruiterName, recruiterId },
  } = useUrlParams();

  const {
    dataTableId,
    isUnitTable,
    isDonorTableConfig,
    lapsedDays,
    retiredDays,
    isRecruiterDonors,
    isRecruiterLocations,
  } = table;

  const { donorDetailsUrl, setLastDonorDetailsUrl } = useRecruiterDashboard();

  const handleBackToDashboard = () => navigate(Routes.DashboardsRecruiter);
  const handleLoadActiveDonors = donorDetailsUrl
    ? () => navigate(donorDetailsUrl)
    : undefined;

  useEffect(() => {
    if (dataTableId) {
      setLastDonorDetailsUrl(location.pathname + location.search);
    }
  }, [dataTableId, location.pathname, location.search, setLastDonorDetailsUrl]);

  if (!dataTableId) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: '#6e7a8a' }}>
              Select a donor list from the Dashboard, or load the Active Donors
              list below.
            </span>
          }
        />
        {handleLoadActiveDonors && (
          <Button
            type="primary"
            icon={<TableOutlined />}
            onClick={handleLoadActiveDonors}
            style={{
              marginTop: 16,
              background: '#0b345f',
              borderColor: '#0b345f',
            }}
          >
            Load Active Donors List
          </Button>
        )}
      </div>
    );
  }

  const baseMeta = buildTableMeta(dataTableId, lapsedDays, retiredDays);

  const meta =
    recruiterName && (isRecruiterDonors || isRecruiterLocations)
      ? {
          ...baseMeta,
          title: `${baseMeta.title} — ${recruiterName}`,
        }
      : baseMeta;

  if (isRecruiterDonors) {
    return (
      <RecruiterDonorsView
        recruiterId={recruiterId}
        dateRange={filters.dateRange}
        meta={meta}
        onBack={handleBackToDashboard}
        onLoadActiveDonors={handleLoadActiveDonors}
      />
    );
  }

  if (isRecruiterLocations) {
    return (
      <RecruiterLocationsView
        recruiterId={recruiterId}
        activeDonorStatuses={filters.activeDonorStatuses}
        meta={meta}
        onBack={handleBackToDashboard}
        onLoadActiveDonors={handleLoadActiveDonors}
      />
    );
  }

  if (isUnitTable) {
    return (
      <UnitTableView
        meta={meta}
        onBack={handleBackToDashboard}
        onLoadActiveDonors={handleLoadActiveDonors}
      />
    );
  }

  if (!isDonorTableConfig) {
    return (
      <div style={{ padding: 0 }}>
        <DonorDetailsHeader
          title={meta.title}
          description={meta.description}
          iconColor={meta.iconColor}
          donorCount={0}
          onBack={handleBackToDashboard}
          onLoadActiveDonors={handleLoadActiveDonors}
        />
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: '#6e7a8a' }}>
              This donor list view is coming soon.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <DonorTableView
      meta={meta}
      onBack={handleBackToDashboard}
      onLoadActiveDonors={handleLoadActiveDonors}
    />
  );
};

export default DonorDetailsStubContent;
